import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface RevisionMessage {
  id: string;
  order_id: string;
  sender_type: 'customer' | 'admin';
  sender_email: string;
  message: string;
  created_at: string;
}

interface DesignRevisionChatProps {
  orderId: string;
  customerEmail: string;
  customerName: string;
  onClose?: () => void;
}

const EMOJI_LIST = [
  '😊', '😍', '👍', '👎', '🙏', '💯', '✅', '❌',
  '🎨', '🖌️', '📐', '✏️', '🔄', '⚠️', '💡', '🤔',
  '😃', '😢', '🎉', '🔥', '⭐', '💪', '👏', '🙌',
];

export default function DesignRevisionChat({ orderId, customerEmail, customerName, onClose }: DesignRevisionChatProps) {
  const [messages, setMessages] = useState<RevisionMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmoji, setShowEmoji] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();

    const subscription = supabase
      .channel(`design-revisions-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'design_revisions',
          filter: `order_id=eq.${orderId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [orderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('design_revisions')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (data) setMessages(data);
    } catch (error) {
      console.error('Mesajlar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('design_revisions')
        .insert({
          order_id: orderId,
          sender_type: 'customer',
          sender_email: customerEmail,
          message: newMessage.trim(),
        });

      if (error) throw error;
      
      setNewMessage('');
      setShowEmoji(false);
    } catch (error) {
      console.error('Mesaj gönderilirken hata:', error);
      alert('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const insertEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-3xl text-orange-500 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Bilgilendirme */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-orange-600 text-xl mt-0.5"></i>
          <div>
            <p className="text-orange-800 font-medium text-sm mb-1">
              Tasarımda değişiklik yapmak istediğiniz noktaları detaylı olarak belirtin
            </p>
            <p className="text-orange-700 text-xs">
              Ekibimiz talebinizi en kısa sürede değerlendirecek ve size geri dönüş yapacaktır.
            </p>
          </div>
        </div>
      </div>

      {/* Mesajlar */}
      {messages.length > 0 && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden mb-4 flex-1">
          <div className="max-h-96 overflow-y-auto p-4 space-y-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%]`}>
                  <div
                    className={`px-4 py-2.5 rounded-lg text-sm whitespace-pre-wrap break-words ${
                      msg.sender_type === 'customer'
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    {msg.sender_type === 'admin' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className="ri-team-line text-xs text-green-600"></i>
                        <span className="text-xs font-medium text-green-600">XKartvizitler Ekibi</span>
                      </div>
                    )}
                    {msg.sender_type === 'customer' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className="ri-user-line text-xs text-orange-200"></i>
                        <span className="text-xs font-medium text-orange-200">Siz</span>
                      </div>
                    )}
                    {msg.message}
                  </div>
                  <div className={`flex items-center gap-1 mt-0.5 ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                    <p className="text-xs text-gray-400">
                      {formatTime(msg.created_at)}
                    </p>
                    <span className="text-xs text-gray-400">✓✓</span>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Mesaj Gönderme Alanı */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-end gap-3">
          <div className="relative flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Düzenleme talebinizi detaylı olarak yazın..."
              rows={3}
              maxLength={500}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 pr-12 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none"
            />
            <div className="absolute right-3 bottom-3" ref={emojiRef}>
              <button
                type="button"
                onClick={() => setShowEmoji(!showEmoji)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
              >
                <i className="ri-emotion-happy-line text-xl"></i>
              </button>
              {showEmoji && (
                <div className="absolute bottom-12 right-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64 z-10">
                  <div className="grid grid-cols-8 gap-1">
                    {EMOJI_LIST.map((emoji, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => insertEmoji(emoji)}
                        className="w-7 h-7 flex items-center justify-center text-lg hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleSend}
            disabled={sending || !newMessage.trim()}
            className="w-12 h-12 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <i className="ri-loader-4-line animate-spin text-xl"></i>
            ) : (
              <i className="ri-send-plane-fill text-xl"></i>
            )}
          </button>
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-gray-500 text-xs">
            <i className="ri-information-line mr-1"></i>
            Enter ile gönder, Shift+Enter ile yeni satır
          </p>
          <p className="text-gray-400 text-xs">{newMessage.length}/500</p>
        </div>
      </div>
    </div>
  );
}
